import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  FileCode,
  Trash2,
  Eye,
  Copy,
  Edit,
  MoreVertical,
  Star,
  StarOff,
  // Clock,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  deleteGeneratedCode,
  updateGeneratedCode,
  type GeneratedCode,
} from "@/services/generatedCodeService";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { CodeEditDialog } from "@/components/silae/code-edit-dialog";
import { cn } from "@/lib/utils";
import { getRubriqueLabel } from "@/data/silae-rubriques";

interface CodeHistoryCardProps {
  code: GeneratedCode;
  onDelete: () => void;
  onUpdate: () => void;
  onEdit: (codeId: string, hasGeneratedCode: boolean) => void;
}

export const CodeHistoryCard: React.FC<CodeHistoryCardProps> = ({
  code,
  onDelete,
  onUpdate,
  onEdit,
}) => {
  const { t, i18n } = useTranslation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);

  const locale = i18n.language === "fr" ? fr : enUS;

  // Handle copy code to clipboard
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(code.generatedCode);
      toast.success(t("history.actions.copySuccess"));
    } catch {
      toast.error(t("history.actions.copyError"));
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteGeneratedCode(code.id);
      toast.success(t("history.actions.deleteSuccess"));
      setShowDeleteDialog(false);
      onDelete();
    } catch (error: unknown) {
      console.error("Error deleting code:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : error &&
              typeof error === "object" &&
              "response" in error &&
              error.response &&
              typeof error.response === "object" &&
              "data" in error.response &&
              error.response.data &&
              typeof error.response.data === "object" &&
              "message" in error.response.data &&
              typeof error.response.data.message === "string"
            ? error.response.data.message
            : undefined;
      toast.error(
        errorMessage ||
          t("history.actions.deleteError") ||
          "Failed to delete code",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle toggle template
  const handleToggleTemplate = async () => {
    try {
      setIsUpdatingTemplate(true);
      await updateGeneratedCode(code.id, {
        isTemplate: !code.isTemplate,
      });
      toast.success(
        code.isTemplate
          ? t("history.actions.unmarkTemplateSuccess")
          : t("history.actions.markTemplateSuccess"),
      );
      onUpdate();
    } catch (error: unknown) {
      console.error("Error toggling template:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : error &&
              typeof error === "object" &&
              "response" in error &&
              error.response &&
              typeof error.response === "object" &&
              "data" in error.response &&
              error.response.data &&
              typeof error.response.data === "object" &&
              "message" in error.response.data &&
              typeof error.response.data.message === "string"
            ? error.response.data.message
            : undefined;
      toast.error(
        errorMessage ||
          t("history.actions.updateError") ||
          "Failed to update code",
      );
    } finally {
      setIsUpdatingTemplate(false);
    }
  };

  const isEmpty =
    code.generatedCode === "" ||
    code.generatedCode === null ||
    code.generatedCode === undefined;

  const createdAt = new Date(code.createdAt);
  // const updatedAt = new Date(code.updatedAt);
  // const isRecentlyUpdated = updatedAt.getTime() > createdAt.getTime();

  return (
    <>
      <Card
        className={cn(
          "group justify-between hover:shadow-lg p-4 transition-all duration-200 border-l-4 min-w-[300px] max-w-[600px] w-full",
          code.isTemplate
            ? "border-l-yellow-500 dark:border-l-yellow-400"
            : "border-l-primary dark:border-l-blue-500",
        )}
      >
        <CardHeader className=" flex flex-col px-0 gap-3 w-full">
          <div className="flex items-start justify-between px-0 gap-3 w-full">
            <CardTitle className="text-lg flex  items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <FileCode className="h-5 w-5 shrink-0 text-primary" />
                <span className="truncate">
                  {code.name.length > 20
                    ? code.name.slice(0, 20) + "..."
                    : code.name}
                </span>
              </div>
              {code.isTemplate && (
                <Badge
                  variant="default"
                  className="ml-auto shrink-0 bg-yellow-500 hover:bg-yellow-600"
                >
                  <Star className="h-3 w-3  fill-current" />
                </Badge>
              )}
              {isEmpty && (
                <Badge
                  variant="default"
                  className="ml-auto shrink-0 bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400"
                >
                  {t("history.card.empty")}
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-start justify-between gap-3">
              {/* Actions Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <MoreVertical className="h-4 w-4" />
                    <span className="sr-only">Actions</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    onClick={() =>
                      onEdit(
                        code.id,
                        !!(
                          code.generatedCode && code.generatedCode.trim() !== ""
                        ),
                      )
                    }
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    {t("history.actions.edit")}
                  </DropdownMenuItem>
                  {/* Only show view/copy actions if generatedCode exists and is not empty */}
                  {code.generatedCode && code.generatedCode.trim() !== "" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setShowCodeDialog(true)}>
                        <Eye className="h-4 w-4 mr-2" />
                        {t("history.actions.viewCode")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleCopyCode}>
                        <Copy className="h-4 w-4 mr-2" />
                        {t("history.actions.copyCode")}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleToggleTemplate}
                    disabled={isUpdatingTemplate}
                    className={cn(
                      "cursor-pointer",
                      code.isTemplate
                        ? "dark:text-yellow-100 text-yellow-900 focus:dark:text-yellow-100 focus:text-yellow-900"
                        : "dark:text-yellow-500 text-yellow-500 focus:dark:text-yellow-500 focus:text-yellow-500",
                    )}
                  >
                    {code.isTemplate ? (
                      <>
                        <StarOff className="h-4 w-4 mr-2 dark:text-yellow-100 text-yellow-900" />
                        {t("history.actions.unmarkTemplate")}
                      </>
                    ) : (
                      <>
                        <Star className="h-4 w-4 mr-2 dark:text-yellow-500 " />
                        {t("history.actions.markTemplate")}
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2 text-destructive" />
                    {t("history.actions.delete")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {/* Tags */}

          {code.tags && code.tags.length > 0 && (
            <div className="flex items-start gap-2">
              {code.tags.slice(0, 5).map((tag: string, index: number) => (
                <Badge
                  key={index}
                  variant="outline"
                  className="text-xs px-2 py-0.5 bg-primary text-white"
                >
                  {tag}
                </Badge>
              ))}
              {code.tags.length > 5 && (
                <Badge
                  variant="outline"
                  className="text-xs px-2 py-0.5 bg-primary text-white"
                >
                  +{code.tags.length - 5}
                </Badge>
              )}
            </div>
          )}
        </CardHeader>

        <CardContent className="px-0 space-y-2">
          {/* Rubriques avec leurs labels */}
          {code.rubriques && code.rubriques.length > 0 && (
            <div className="px-0 flex gap-2 items-center">
              <p className="text-xs font-semibold text-muted-foreground px-0">
                Rubriques:
              </p>
              <div className="flex flex-wrap gap-2">
                {code.rubriques
                  .slice(0, 3)
                  .map((rubrique: string, index: number) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="text-xs px-2 py-1 rounded-md bg-slate-200 dark:bg-slate-700 text-foreground"
                      title={rubrique}
                    >
                      <span className="font-mono text-xs font-bold mr-1">
                        {rubrique}
                      </span>
                      <span className="truncate max-w-[150px]">
                        {getRubriqueLabel(rubrique)}
                      </span>
                    </Badge>
                  ))}
                {code.rubriques.length > 3 && (
                  <Badge
                    variant="secondary"
                    className="text-xs px-2 py-1 bg-slate-200 dark:bg-slate-700"
                  >
                    +{code.rubriques.length - 3}
                  </Badge>
                )}
              </div>
            </div>
          )}
          {code.description && (
            <div className="space-y-4 pt-0 grow bg-slate-200 p-2 dark:bg-slate-900 rounded-md border border-slate-300 dark:border-slate-700 ">
              {code.description ? (
                <p className="text-sm text-muted-foreground   line-clamp-2">
                  {code.description}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground  line-clamp-2">
                  {t("history.card.noDescription")}
                </p>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter className="px-0">
          {/* Metadata Row */}
          <div className="flex flex-wrap items-center justify-end w-full gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              <span className="whitespace-nowrap">
                {format(createdAt, "PPP", { locale })}
              </span>
            </div>

            {/* {isRecentlyUpdated && (
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                <span className="whitespace-nowrap">
                  {formatDistanceToNow(updatedAt, {
                    addSuffix: true,
                    locale,
                  })}
                </span>
              </div>
            )} */}
          </div>
        </CardFooter>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("history.deleteDialog.title")}</DialogTitle>
            <DialogDescription>
              {t("history.deleteDialog.description")}
              <br />
              <strong>{code.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? t("common.deleting") : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Code Dialog with Code Editor */}
      <CodeEditDialog
        isOpen={showCodeDialog}
        onOpenChange={setShowCodeDialog}
        initialCode={code.generatedCode}
        readOnly={true}
        onCopy={async (codeToCopy: string) => {
          try {
            await navigator.clipboard.writeText(codeToCopy);
            toast.success(t("history.actions.copySuccess"));
          } catch {
            toast.error(t("history.actions.copyError"));
          }
        }}
        title={code.name}
        description={code.description || undefined}
        placeholder={t("codeEditDialog.placeholder")}
        language="silae"
      />
    </>
  );
};
